import {
    fetchNotifications,
    fetchUnreadCount,
    markNotificationRead,
    markNotificationUnread,
    markAllNotificationsRead,
    fetchMenuBadges,
} from '@/api/notifications';

// Pastilles de menu : une entrée de route déclare `meta.badgeKey` et le
// SidebarItem affiche le compteur de même clé, tel que le renvoie
// `GET /api/menu-badges` (tickets, facturesAchat, projets, stock, provisioning).

// Store du centre de notifications (cloche) ET des pastilles de menu. Le
// polling et le cycle de vie sont pilotés par le composable useNotifications ;
// ce module ne fait que l'état.
//
// Deux compteurs de nature DIFFÉRENTE cohabitent ici :
//  - unreadCount : personnel (mes notifications non lues) ;
//  - menuBadges  : par entrée de menu. Collectif pour les tickets et les
//    factures (un ticket pris en charge, une facture ouverte, et la pastille
//    s'éteint pour tout le service), personnel pour projets, stock et
//    provisioning (mes notifications non lues du domaine).
export default {
    namespaced: true,
    state: () => ({
        items: [],
        unreadCount: 0,
        loading: false,
        menuBadges: {},
    }),
    getters: {
        unreadCount: (state) => state.unreadCount,
        items: (state) => state.items,
        menuBadges: (state) => state.menuBadges,
    },
    mutations: {
        SET_ITEMS(state, items) { state.items = items; },
        SET_UNREAD_COUNT(state, count) { state.unreadCount = count; },
        SET_LOADING(state, value) { state.loading = value; },
        MARK_READ(state, id) {
            const n = state.items.find((i) => i.id === id);
            if (n && !n.read_at) {
                n.read_at = new Date().toISOString();
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        },
        MARK_UNREAD(state, id) {
            const n = state.items.find((i) => i.id === id);
            if (n && n.read_at) {
                n.read_at = null;
                state.unreadCount += 1;
            }
        },
        MARK_ALL_READ(state) {
            const now = new Date().toISOString();
            state.items.forEach((i) => { if (!i.read_at) i.read_at = now; });
            state.unreadCount = 0;
        },
        SET_MENU_BADGES(state, badges) {
            state.menuBadges = { ...badges };
        },
        RESET(state) {
            state.items = [];
            state.unreadCount = 0;
            state.loading = false;
            state.menuBadges = {};
        },
    },
    actions: {
        async fetchUnreadCount({ commit }) {
            const res = await fetchUnreadCount();
            commit('SET_UNREAD_COUNT', res?.unread ?? 0);
        },
        async fetchList({ commit }, params = {}) {
            commit('SET_LOADING', true);
            try {
                const res = await fetchNotifications(params);
                commit('SET_ITEMS', res?.data || []);
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async markRead({ commit, dispatch }, id) {
            const res = await markNotificationRead(id);
            commit('MARK_READ', id);
            // Le compteur renvoyé par le serveur fait foi : le décompte local
            // dérive dès qu'un autre onglet, ou la clôture collective d'un
            // service, a déjà touché la même notification.
            if (typeof res?.unread === 'number') commit('SET_UNREAD_COUNT', res.unread);
            dispatch('fetchMenuBadges').catch(() => {});

            return res;
        },
        async markUnread({ commit, dispatch }, id) {
            const res = await markNotificationUnread(id);
            commit('MARK_UNREAD', id);
            if (typeof res?.unread === 'number') commit('SET_UNREAD_COUNT', res.unread);
            dispatch('fetchMenuBadges').catch(() => {});

            return res;
        },
        async markAllRead({ commit, dispatch }) {
            await markAllNotificationsRead();
            commit('MARK_ALL_READ');
            dispatch('fetchMenuBadges').catch(() => {});
        },
        /**
         * Rafraîchit toutes les pastilles du menu. Le serveur ne renvoie que les
         * clés de l'appelant (une facture qu'il ne peut pas voir n'y figure
         * pas) : l'état est remplacé en bloc, une clé disparue s'éteint.
         */
        async fetchMenuBadges({ commit }) {
            const res = await fetchMenuBadges();
            commit('SET_MENU_BADGES', res || {});
        },
    },
};
